import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { KeyRound, Layers, Unlink } from "lucide-react";
import api from "../../api/api";

/**
 * /join/<code> — the shareable link behind a program's invitation code, or a
 * student's own license code from their organization (kind "license").
 * Signed-in students go straight to My Coaching → Programs with the code filled
 * in. Everyone else sees which program (or organization) it is and signs up; the
 * code rides along to /signup so it's used as soon as their profile step is done.
 * Students who log in instead (or still have signup steps left) are brought back
 * to it after (App.js, utils/pendingJoin).
 */
export default function JoinProgram({ isAuthed, isStudent }) {
    const { code } = useParams();
    const [program, setProgram] = useState(null);
    const [loading, setLoading] = useState(true);
    const [invalid, setInvalid] = useState(false); // true, or "used": a license code someone already redeemed

    useEffect(() => {
        if (isAuthed) return;
        let cancelled = false;
        api.get(`/org/programs/preview?code=${encodeURIComponent(code)}`)
            // A license code previews as { kind: "license", OrgName, ProgramName } (no names of the student)
            .then((res) => { if (!cancelled) setProgram(res.data.kind === "license" ? res.data : res.data.program); })
            .catch((err) => { if (!cancelled) setInvalid(err.response?.data?.code === "CODE_USED" ? "used" : true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [code, isAuthed]);

    if (isAuthed && isStudent) {
        return <Navigate to={`/coaching?tab=programs&code=${encodeURIComponent(code)}`} replace />;
    }
    const isLicense = program?.kind === "license";

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <main className="mx-auto max-w-lg px-4 py-16">
                <div className="rounded-3xl border border-slate-100 bg-white/90 p-6 text-center shadow-2xl shadow-primary-200/20 sm:p-10">
                    {isAuthed ? (
                        <>
                            {/* old <h1 className="mb-2 text-2xl font-extrabold">Invitation links are for students</h1> */}
                            <h1 className="mb-2 text-2xl font-extrabold">Invitation links are for clients</h1>
                            {/* old <p className="mb-8 text-slate-500">This link adds a student account to a program. Sign out and create a student account to use it.</p> */}
                            {/* (since Oct 30 a link is a license code's, not a program's: it puts the license on a client account) */}
                            <p className="mb-8 text-slate-500">This link gives a client account a license from their school or organization. Sign out and create a client account to use it.</p>
                            <Link to="/" className="font-semibold text-primary-600 hover:text-primary-700">Back to home</Link>
                        </>
                    ) : loading ? (
                        <div aria-busy="true" aria-label="Loading invitation">
                            <Skeleton circle width={48} height={48} className="mb-4" />
                            <Skeleton width="60%" height={30} className="mb-3" />
                            <Skeleton count={2} height={16} className="mb-2" />
                            <Skeleton height={48} borderRadius={12} className="mt-6" />
                        </div>
                    ) : invalid === "used" ? (
                        <>
                            {/* the owner's license is already on their account; anyone else needs their own code */}
                            <KeyRound size={40} className="mx-auto mb-4 text-slate-400" />
                            <h1 className="mb-2 text-2xl font-extrabold">This code has already been used</h1>
                            <p className="mb-8 text-slate-500">
                                If it's your code, log in. Your license is already on your account.
                                If it isn't yours, ask your school or organization for your own code.
                            </p>
                            <Link to="/login"
                                  className="flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-700">
                                Log in
                            </Link>
                            <Link to="/" className="mt-6 inline-block font-semibold text-primary-600 hover:text-primary-700">Back to home</Link>
                        </>
                    ) : invalid ? (
                        <>
                            <Unlink size={40} className="mx-auto mb-4 text-slate-400" />
                            <h1 className="mb-2 text-2xl font-extrabold">This invitation link doesn't work</h1>
                            {/* old <p className="mb-8 text-slate-500">The link may have expired, the code was changed, or the program has ended. Ask your school or organization for a new link.</p> */}
                            {/* license links only: a program's old invitation link lands here too (Alicia, Oct 30), and the organization adds them */}
                            <p className="mb-8 text-slate-500">The link may be old, or its license was removed. Ask your school or organization to add you, or for a new link.</p>
                            <Link to="/" className="font-semibold text-primary-600 hover:text-primary-700">Back to home</Link>
                        </>
                    ) : (
                        <>
                            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                                {isLicense ? <KeyRound size={24} /> : <Layers size={24} />}
                            </span>
                            {isLicense ? (
                                <>
                                    <h1 className="mb-2 text-3xl font-extrabold">{program.OrgName} gave you a license</h1>
                                    <p className="mb-8 text-slate-500">
                                        Create your Pono Pilot account to use it
                                        {program.ProgramName && <> and join <span className="font-semibold text-slate-800">{program.ProgramName}</span></>}.
                                        {/* only when the license comes with a plan */}
                                        {program.PlanName && <>{" "}{program.OrgName} covers your {program.PlanName} plan while the license is active.</>}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h1 className="mb-2 text-3xl font-extrabold">You're invited</h1>
                                    <p className="mb-8 text-slate-500">
                                        Join <span className="font-semibold text-slate-800">{program.ProgramName}</span> at{" "}
                                        <span className="font-semibold text-slate-800">{program.OrgName}</span> on Pono Pilot.
                                    </p>
                                </>
                            )}
                            <Link to={`/signup?code=${encodeURIComponent(code)}`}
                                  className="flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-700">
                                Create your account
                            </Link>
                            <p className="mt-6 text-sm text-slate-500">
                                Already have an account?{" "}
                                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">Log in</Link>
                                {" "}and we'll bring you back to {isLicense ? "use it" : "join"}. You can also enter code{" "}
                                <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono font-semibold text-slate-800">{code.toUpperCase()}</code>{" "}
                                under My Coaching → Programs.
                            </p>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
